import { useEffect } from 'react';
import { attachGeneration } from './generation/navigatorGenerationStore';
import { useNavigatorGeneration } from './generation/useNavigatorGeneration';
import type { MatchedSpecialtyApi, MatchingStatus } from './navigator-matching.types';

export type NavigatorMatchingResult = {
  status: MatchingStatus;
  categories: MatchedSpecialtyApi[];
};

const NO_CATEGORIES: MatchedSpecialtyApi[] = [];

/**
 * Store-backed matching hook, keyed by a navigator session id. Attaches to the
 * shared generation store: when the chat already kicked the full generation
 * (Care Guide early-start) this subscribes to the in-flight/ready run, so
 * results render instantly; otherwise (deep link, reload, hub revisit) it
 * starts the matching-only pipeline — kick /care-options, poll
 * /find-providers/status, load specialties, auto-build the care plan.
 *
 * Shared by the Care Seeker results page and the FP Q&A results page so both
 * read the SAME live navigator data (identical categories, order, providers).
 * attachGeneration is idempotent, so StrictMode double-mounts are safe.
 */
export const useNavigatorMatching = (sessionId?: string): NavigatorMatchingResult => {
  useEffect(() => {
    if (sessionId) attachGeneration(sessionId);
  }, [sessionId]);

  const state = useNavigatorGeneration(sessionId);
  if (!sessionId) {
    return { status: 'idle', categories: NO_CATEGORIES };
  }
  return {
    status: state?.status ?? 'running',
    categories: state?.categories ?? NO_CATEGORIES,
  };
};
